<template>
  <div class="padding-10">
    <div class="d-flex align-center mb-4 flex-wrap">
      <v-tabs v-model="tab" background-color="transparent" active-class="primary--text font-weight-bold">
        <v-tab>All Logs ({{ logs.length }})</v-tab>
        <v-tab>Key Category Logs ({{ categoryLogsCount }})</v-tab>
        <v-tab>Type of Key Logs ({{ typeLogsCount }})</v-tab>
      </v-tabs>

      <v-spacer></v-spacer>

      <div class="d-flex align-center mt-2 mt-sm-0 flex-wrap" style="gap: 8px;">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search"
          dense
          outlined
          clearable
          hide-details
          style="min-width: 240px;"
        ></v-text-field>

        <v-menu
          v-model="dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="dateRangeText"
              prepend-inner-icon="mdi-calendar"
              label="Date Range"
              dense
              outlined
              readonly
              clearable
              hide-details
              style="min-width: 240px;"
              v-bind="attrs"
              v-on="on"
              @click:clear="dateRange = []"
            ></v-text-field>
          </template>
          <v-date-picker v-model="dateRange" no-title scrollable range :max="maxDate">
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="dateRange = []">Clear</v-btn>
            <v-btn text color="primary" @click="dateMenu = false">OK</v-btn>
          </v-date-picker>
        </v-menu>

        <v-select
          v-model="actionFilter"
          :items="actionFilterItems"
          label="Action"
          dense
          outlined
          hide-details
          style="max-width: 160px;"
        ></v-select>

        <v-btn color="primary" depressed @click="fetchLogs" :loading="loading">
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>
    </div>

    <v-card :elevation="$root.ele">
      <v-data-table
        :headers="headers"
        :items="filteredList"
        :loading="loading"
        item-key="id"
        class="templates-table key-logs-table"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100, -1],
        }"
        :items-per-page="25"
        sort-by="rawDate"
        :sort-desc="true"
      >
        <template v-slot:item.logType="{ item }">
          <v-chip
            small
            :color="item.logType === 'Key Category' ? 'indigo lighten-4' : 'teal lighten-4'"
            :text-color="item.logType === 'Key Category' ? 'indigo darken-3' : 'teal darken-3'"
            class="font-weight-medium"
          >
            {{ item.logType }}
          </v-chip>
        </template>

        <template v-slot:item.action="{ item }">
          <v-chip
            small
            :color="getActionColor(item.action)"
            text-color="white"
            class="font-weight-bold text-uppercase"
          >
            {{ item.action }}
          </v-chip>
        </template>

        <template v-slot:item.details="{ item }">
          <!-- ADD ACTION -->
          <div v-if="item.action === 'add'" class="py-2">
            <div class="pa-2 rounded green lighten-5" style="border: 1px solid #c8e6c9; min-width: 240px; display: inline-block;">
              <div class="caption green--text text--darken-4 font-weight-bold mb-1">ADDED OPTION</div>
              <div class="body-2"><span class="grey--text text--darken-2 font-weight-medium">Name:</span> <span class="font-weight-bold green--text text--darken-3">{{ item.name }}</span></div>
              <div class="body-2"><span class="grey--text text--darken-2 font-weight-medium">Value:</span> <span class="font-weight-bold grey--text text--darken-4">{{ item.value || '-' }}</span></div>
            </div>
          </div>

          <!-- RENAME / EDIT ACTION -->
          <div v-else-if="item.action === 'rename'" class="py-2">
            <div class="d-flex align-center flex-wrap" style="gap: 12px;">
              <!-- Previous Block -->
              <div class="pa-2 rounded grey lighten-4" style="border: 1px solid #e0e0e0; min-width: 220px;">
                <div class="caption grey--text text--darken-2 font-weight-bold mb-1">PREVIOUS OPTION</div>
                <div class="body-2"><span class="grey--text text--darken-2 font-weight-medium">Name:</span> <span class="font-weight-bold text-decoration-line-through grey--text text--darken-3">{{ item.previousName || item.name }}</span></div>
                <div class="body-2"><span class="grey--text text--darken-2 font-weight-medium">Value:</span> <span class="grey--text text--darken-3">{{ item.previousValue || item.from || '-' }}</span></div>
              </div>

              <!-- New Block -->
              <div class="pa-2 rounded blue lighten-5" style="border: 1px solid #bbdefb; min-width: 220px;">
                <div class="caption primary--text font-weight-bold mb-1">NEW OPTION</div>
                <div class="body-2"><span class="primary--text font-weight-medium">Name:</span> <strong class="primary--text font-weight-bold">{{ item.newName || item.name }}</strong></div>
                <div class="body-2"><span class="primary--text font-weight-medium">Value:</span> <span class="primary--text font-weight-bold">{{ item.newValue || item.to || item.value || '-' }}</span></div>
              </div>
            </div>
          </div>

          <!-- DELETE ACTION -->
          <div v-else-if="item.action === 'delete'" class="py-2">
            <div class="pa-2 rounded red lighten-5" style="border: 1px solid #ffcdd2; min-width: 240px; display: inline-block;">
              <div class="caption red--text text--darken-3 font-weight-bold mb-1">DELETED OPTION</div>
              <div class="body-2"><span class="red--text text--darken-2 font-weight-medium">Name:</span> <span class="font-weight-bold red--text text--darken-3 text-decoration-line-through">{{ item.previousName || item.name }}</span></div>
              <div class="body-2"><span class="red--text text--darken-2 font-weight-medium">Value:</span> <span class="red--text text--darken-3 font-weight-medium">{{ item.previousValue || item.value || '-' }}</span></div>
            </div>
          </div>

          <!-- FALLBACK -->
          <div v-else class="py-2 body-2">
            <div class="font-weight-bold">{{ item.name || item.previousName || '-' }}</div>
            <div v-if="item.value || item.previousValue" class="grey--text text--darken-2">
              Value: {{ item.value || item.previousValue }}
            </div>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            v-if="item.status"
            small
            :color="item.status === 'failed' ? 'red darken-1' : 'green darken-1'"
            text-color="white"
            class="font-weight-bold"
          >
            {{ item.status === "failed" ? "Failed" : "Success" }}
          </v-chip>
          <span v-else>-</span>
        </template>

        <template v-slot:item.message="{ item }">
          <span :class="{ 'red--text': item.status === 'failed' }">{{ item.message || '-' }}</span>
        </template>

        <template v-slot:item.performedBy="{ item }">
          <span>{{ item.performedBy || 'unknown' }}</span>
        </template>

        <template v-slot:item.ip="{ item }">
          <span>{{ item.ip || 'unknown' }}</span>
        </template>

        <template v-slot:item.formattedDate="{ item }">
          <span>{{ item.formattedDate }}</span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { keyCategoryLogs, keyTypeLogs } from "../assets/js/firebase";

export default {
  name: "KeyLogs",
  data() {
    return {
      tab: 0,
      loading: true,
      logs: [],
      categoryLogsList: [],
      typeLogsList: [],
      unsubCategory: null,
      unsubType: null,
      search: "",
      dateMenu: false,
      dateRange: [],
      actionFilter: "all",
      actionFilterItems: [
        { text: "All", value: "all" },
        { text: "Add", value: "add" },
        { text: "Rename", value: "rename" },
        { text: "Delete", value: "delete" },
      ],
    };
  },
  computed: {
    headers() {
      let headersList = [
        { text: "Log Source", value: "logType" },
        { text: "Action", value: "action" },
        { text: "Option Details", value: "details", sortable: false, width: "520px", class: "details-col", cellClass: "details-col" },
        { text: "Status", value: "status" },
        { text: "Message", value: "message" },
        { text: "Performed By", value: "performedBy" },
        { text: "IP Address", value: "ip" },
        { text: "Date & Time", value: "formattedDate" },
      ];
      return headersList.map((h) => ({
        ...h,
        class: "text-capitalize",
        align: "start",
        sortable: h.sortable !== undefined ? h.sortable : true,
        divider: true,
      }));
    },
    categoryLogsCount() {
      return this.categoryLogsList.length;
    },
    typeLogsCount() {
      return this.typeLogsList.length;
    },
    filteredList() {
      let list = [];
      if (this.tab === 1 || this.tab === "1") {
        list = [...this.categoryLogsList];
      } else if (this.tab === 2 || this.tab === "2") {
        list = [...this.typeLogsList];
      } else {
        list = [...this.logs];
      }

      const [start, end] = [...this.dateRange].sort();
      if (start) {
        const from = moment(start).startOf("day").valueOf();
        const to = moment(end || start).endOf("day").valueOf();
        list = list.filter((item) => item.rawDate >= from && item.rawDate <= to);
      }

      if (this.actionFilter !== "all") {
        list = list.filter((item) => (item.action || "").toLowerCase() === this.actionFilter);
      }

      const query = (this.search || "").trim().toLowerCase();
      if (query) {
        const fields = [
          "logType",
          "action",
          "name",
          "value",
          "previousName",
          "previousValue",
          "newName",
          "newValue",
          "from",
          "to",
          "status",
          "message",
          "performedBy",
          "ip",
          "formattedDate",
        ];
        list = list.filter((item) =>
          fields.some((f) => String(item[f] || "").toLowerCase().includes(query))
        );
      }

      return list.sort((a, b) => b.rawDate - a.rawDate);
    },
    maxDate() {
      return moment().format("YYYY-MM-DD");
    },
    dateRangeText() {
      return [...this.dateRange]
        .sort()
        .map((d) => moment(d).format("DD MMM YYYY"))
        .join(" - ");
    },
  },
  methods: {
    getActionColor(action) {
      switch ((action || "").toLowerCase()) {
        case "add":
        case "addition":
          return "green darken-1";
        case "delete":
        case "deletion":
          return "red darken-1";
        case "rename":
        case "edit":
          return "blue darken-1";
        default:
          return "grey darken-1";
      }
    },
    getRawTimestamp(timestamp) {
      if (!timestamp) return 0;
      if (timestamp.toDate && typeof timestamp.toDate === "function") {
        return timestamp.toDate().getTime();
      }
      if (timestamp.seconds) {
        return timestamp.seconds * 1000;
      }
      return new Date(timestamp).getTime() || 0;
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "-";
      let dateObj = null;
      if (timestamp.toDate && typeof timestamp.toDate === "function") {
        dateObj = timestamp.toDate();
      } else if (timestamp.seconds) {
        dateObj = new Date(timestamp.seconds * 1000);
      } else {
        dateObj = new Date(timestamp);
      }
      const timeMs = new Date(dateObj).getTime();
      if (isNaN(timeMs)) return "-";
      return moment(dateObj).format("DD MMM YYYY, hh:mm:ss A");
    },
    updateCombinedLogs() {
      const allLogs = [...this.categoryLogsList, ...this.typeLogsList];
      allLogs.sort((a, b) => b.rawDate - a.rawDate);
      this.logs = allLogs;
      console.log("[KeyLogs] Combined logs total:", this.logs.length, "| Category:", this.categoryLogsList.length, "| Type:", this.typeLogsList.length);
    },
    listenLogs() {
      this.loading = true;
      const vm = this;

      if (vm.unsubCategory) vm.unsubCategory();
      if (vm.unsubType) vm.unsubType();

      vm.unsubCategory = keyCategoryLogs.onSnapshot(
        (snap) => {
          console.log("[KeyLogs] keyCategoryLogs snapshot count:", snap.docs.length);
          vm.categoryLogsList = snap.docs
            .map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                logType: "Key Category",
                ...data,
                formattedDate: vm.formatTimestamp(data.performedAt),
                rawDate: vm.getRawTimestamp(data.performedAt),
              };
            })
            .sort((a, b) => b.rawDate - a.rawDate);
          vm.updateCombinedLogs();
          vm.loading = false;
        },
        (err) => {
          console.error("[KeyLogs] Error reading keyCategoryLogs:", err);
          vm.loading = false;
        }
      );

      vm.unsubType = keyTypeLogs.onSnapshot(
        (snap) => {
          console.log("[KeyLogs] keyTypeLogs snapshot count:", snap.docs.length);
          vm.typeLogsList = snap.docs
            .map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                logType: "Type of Key",
                ...data,
                formattedDate: vm.formatTimestamp(data.performedAt),
                rawDate: vm.getRawTimestamp(data.performedAt),
              };
            })
            .sort((a, b) => b.rawDate - a.rawDate);
          vm.updateCombinedLogs();
          vm.loading = false;
        },
        (err) => {
          console.error("[KeyLogs] Error reading keyTypeLogs:", err);
          vm.loading = false;
        }
      );
    },
    fetchLogs() {
      this.listenLogs();
    },
  },
  mounted() {
    this.listenLogs();
  },
  beforeDestroy() {
    if (this.unsubCategory) this.unsubCategory();
    if (this.unsubType) this.unsubType();
  },
};
</script>

<style scoped>
.padding-10 {
  padding: 10px;
}
.key-logs-table >>> table {
  table-layout: auto !important;
}
.key-logs-table >>> .details-col {
  min-width: 520px;
  width: 520px;
}
</style>
